// import type { AssetResultMatch } from '@frameio/graphql-client/generated';
import clsx from 'clsx';
import { Text, useActiveWindowBreakpoint } from '@frameio/vapor';
import { FieldMatch } from '../../../../../shared/components';
import { TextFieldMatch } from '../../../../../shared/components/FieldMatch/components/TextFieldMatch';
import type { AssetResultMatch } from '../../../../../shared/types';
import { getMatchLabel } from '../../../../../shared/utils/getMatchLabel';
import styles from './FeaturedField.module.css';

type FeaturedFieldProps =
  | {
      fieldMatch: AssetResultMatch;
      isActive: boolean;
      matchIndex?: number;
    }
  | {
      children: React.ReactNode;
      isActive: boolean;
      style?: React.CSSProperties;
    };

const MATCHES_WITH_CHIP_UI: Array<AssetResultMatch['__typename']> = [
  'KeywordsMatch',
  'StatusMatch',
  'AssigneeMatch',
  'CreatorMatch',
];

export const FeaturedField = (props: FeaturedFieldProps) => {
  if ('children' in props) {
    return (
      <MatchWrapper isActive={props.isActive} style={props?.style}>
        {props.children}
      </MatchWrapper>
    );
  }

  const { fieldMatch } = props;
  const matchType = fieldMatch?.__typename;
  const hasChipUI = MATCHES_WITH_CHIP_UI.includes(matchType);

  // @hack see FeaturedFieldMatches.tsx for more details
  if (matchType === 'TranscriptionResultMatch') {
    const snippet = fieldMatch.snippets?.[0];

    return (
      <MatchWrapper key={`${matchType}-${snippet}`} isActive={props.isActive}>
        <div className={styles.captionLabel}>
          <Text color="secondary" fontWeight="semiBold" variant="caption">
            {getMatchLabel(fieldMatch)}
          </Text>
        </div>
        <div
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          <TextFieldMatch
            highlights={(fieldMatch.terms as string[]) ?? undefined}
            text={snippet ?? ''}
            truncate={true}
          />
        </div>
      </MatchWrapper>
    );
  }

  const matchLabel = getMatchLabel(fieldMatch);

  return (
    <MatchWrapper
      hasChipUI={hasChipUI}
      hasGap={matchType !== 'VisualMatch'}
      isActive={props.isActive}
    >
      {matchLabel && (
        <div className={styles.captionLabel}>
          <Text color="secondary" fontWeight="medium" variant="caption">
            {matchLabel}
          </Text>
        </div>
      )}
      <div className={styles.fieldMatchContainer}>
        <FieldMatch fieldMatch={fieldMatch} variant="inline" />
      </div>
    </MatchWrapper>
  );
};

export const MatchWrapper = ({
  children,
  hasChipUI = false,
  hasGap = true,
  isActive = false,
  style,
}: {
  children: React.ReactNode;
  hasChipUI?: boolean;
  hasGap?: boolean;
  isActive: boolean;
  style?: React.CSSProperties;
}) => {
  const breakpoint = useActiveWindowBreakpoint();
  const isMobile = breakpoint === 'base';

  if (isMobile && !children) return null;

  return (
    <div
      className={clsx(styles.matchWrapper, isActive && styles.active)}
      data-has-chip-ui={hasChipUI}
      data-has-gap={hasGap}
      data-mobile={isMobile}
      style={style}
    >
      {children}
    </div>
  );
};
